/**
 * Presentation helpers for the read-time adoption layer (Contract 7).
 */

import type { ExtensionType } from "../types/index";

export const SHARED_USAGE_NOTE_SHORT =
  "This skill name is shared with other skills; count reflects combined usage, not this skill alone.";
export const SHARED_USAGE_NOTE_LONG =
  "This skill's name is duplicated elsewhere in the catalog. Usage reflects the combined total of active users across all skills with this name.";

// A plugin's flag is usually driven by a bundled skill's name colliding with a skill
// elsewhere, not by the plugin's own name — so the copy must not imply the plugin
// itself is the duplicate (which reads as wrong when no like-named plugin exists).
export const SHARED_USAGE_NOTE_PLUGIN_SHORT =
  "A skill in this plugin shares its name with other skills; count reflects combined usage, not this plugin alone.";
export const SHARED_USAGE_NOTE_PLUGIN_LONG =
  "One or more skills in this plugin share a name with other skills elsewhere in the catalog. Usage reflects the combined total of active users across all skills with those names.";

/** The shared-usage copy for a surface variant, tailored to the entry type. */
export function sharedUsageNoteCopy(variant: "short" | "long", extensionType?: ExtensionType): string {
  const isPlugin = extensionType === "plugin";
  if (variant === "long") return isPlugin ? SHARED_USAGE_NOTE_PLUGIN_LONG : SHARED_USAGE_NOTE_LONG;
  return isPlugin ? SHARED_USAGE_NOTE_PLUGIN_SHORT : SHARED_USAGE_NOTE_SHORT;
}

/**
 * Render Contract 7's `dataThroughDate` ("YYYY-MM-DD") as a readable date.
 *
 * Parses the components explicitly instead of `new Date(iso)`: the spec parses a
 * date-ONLY string as UTC midnight, so in any negative-offset timezone
 * `toLocaleDateString()` renders the PREVIOUS day — "2026-08-01" would display as
 * Jul 31 across the US. Building the Date from local components avoids that shift.
 * (The `formatDate` helpers on the card and list view take full ISO timestamps with
 * a `Z`, so they are not affected; this one is.)
 *
 * Returns "" for anything unparseable so callers can omit the caption entirely
 * rather than surface "Invalid Date" to users.
 */
export function formatDataThroughDate(dataThroughDate: string): string {
  const [year, month, day] = dataThroughDate.split("-").map(Number);
  if (!year || !month || !day) return "";
  const parsed = new Date(year, month - 1, day);
  if (isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
